import { FiltersType } from '@/api/types';
import { client } from '@/client';
import {
  AccountsSchedulesRequest,
  BranchesSchedulesRequest,
  ChangeScheduleDateRequest,
  ChangeScheduleEnrollTypeRequest,
  DeleteScheduleRequest,
  GroupsSchedulesRequest,
  ListAccountScheduleResponse,
  ListCourseScheduleResponse,
  UsersSchedulesRequest,
} from '@/client/courses';
import { MessageResponseModel } from '@/client/models';
import {
  ChangeScheduleDateMutation,
  DeleteScheduleMutation,
  GenericForbiddenApiError,
  SaveAccountScheduleMutation,
  SaveBranchesSchedulesMutation,
  SaveGroupsSchedulesMutation,
  SaveUsersSchedulesMutation,
} from '@/hooks/query';
import { COURSES_QUERY_KEY } from '@/hooks/query/courses.query-keys';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

export const useCourseSchedules = (
  params: UseQueryOptions & {
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  },
  courseId?: string,
): UseQueryResult<ListCourseScheduleResponse> & {
  schedules: ListCourseScheduleResponse | undefined;
} => {
  const {
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [
      `${COURSES_QUERY_KEY}Schedule`,
      take,
      skip,
      JSON.stringify(sort),
      JSON.stringify(filters),
    ],
    () => client.courses.getCourseSchedules(params, courseId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    schedules: data,
    ...(rest as UseQueryResult<ListCourseScheduleResponse>),
  };
};

export const useAccountSchedules = (
  params: UseQueryOptions & {
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  },
  accountId?: string,
): UseQueryResult<ListAccountScheduleResponse> & {
  schedules: ListAccountScheduleResponse | undefined;
} => {
  const {
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [
      `${COURSES_QUERY_KEY}AccountSchedule`,
      take,
      skip,
      JSON.stringify(sort),
      JSON.stringify(filters),
    ],
    () => client.courses.getAccountSchedules(params, accountId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    schedules: data,
    ...(rest as UseQueryResult<ListAccountScheduleResponse>),
  };
};

export const useAccountsSchedules = (): {
  add: (payload: AccountsSchedulesRequest) => Promise<MessageResponseModel>;
} & SaveAccountScheduleMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    AccountsSchedulesRequest
  >('saveAccountSchedule', (payload: AccountsSchedulesRequest) =>
    client.courses.saveAccountSchedule({
      courses: payload.courses,
      accounts: payload.accounts,
      date: payload.date,
      autoEnroll: payload.autoEnroll,
      type: payload.type,
    }),
  );

  return { add: mutateAsync, ...(rest as any) };
};

export const useBranchesSchedules = (): {
  add: (payload: BranchesSchedulesRequest) => Promise<MessageResponseModel>;
} & SaveBranchesSchedulesMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    BranchesSchedulesRequest
  >('saveBranchesSchedules', (payload: BranchesSchedulesRequest) =>
    client.courses.saveBranchesSchedules({
      courses: payload.courses,
      branches: payload.branches,
      date: payload.date,
      autoEnroll: payload.autoEnroll,
      type: payload.type,
    }),
  );

  return { add: mutateAsync, ...(rest as any) };
};

export const useGroupsSchedules = (): {
  add: (payload: GroupsSchedulesRequest) => Promise<MessageResponseModel>;
} & SaveGroupsSchedulesMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    GroupsSchedulesRequest
  >('saveGroupsSchedules', (payload: GroupsSchedulesRequest) =>
    client.courses.saveGroupsSchedules({
      courses: payload.courses,
      groups: payload.groups,
      date: payload.date,
      autoEnroll: payload.autoEnroll,
      type: payload.type,
    }),
  );

  return { add: mutateAsync, ...(rest as any) };
};

export const useUsersSchedules = (): {
  add: (payload: UsersSchedulesRequest) => Promise<MessageResponseModel>;
} & SaveUsersSchedulesMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    UsersSchedulesRequest
  >('saveUsersSchedules', (payload: UsersSchedulesRequest) =>
    client.courses.saveUsersSchedules({
      courses: payload.courses,
      users: payload.users,
      date: payload.date,
      type: payload.type,
    }),
  );

  return { add: mutateAsync, ...(rest as any) };
};

export const useChangeScheduleDate = (): {
  edit: (payload: ChangeScheduleDateRequest) => Promise<MessageResponseModel>;
} & ChangeScheduleDateMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    ChangeScheduleDateRequest
  >('changeScheduleDate', (payload: ChangeScheduleDateRequest) =>
    client.courses.changeScheduleDate({
      scheduleId: payload.scheduleId,
      scheduleType: payload.scheduleType,
      date: payload.date,
      type: payload.type,
    }),
  );

  return { edit: mutateAsync, ...(rest as any) };
};

export const useChangeScheduleEnrollType = (): {
  edit: (
    payload: ChangeScheduleEnrollTypeRequest,
  ) => Promise<MessageResponseModel>;
} & ChangeScheduleDateMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    ChangeScheduleEnrollTypeRequest
  >('changeScheduleEnrollType', (payload: ChangeScheduleEnrollTypeRequest) =>
    client.courses.changeScheduleEnrollType({
      scheduleId: payload.scheduleId,
      scheduleType: payload.scheduleType,
      autoEnroll: payload.autoEnroll,
      type: payload.type,
    }),
  );

  return { edit: mutateAsync, ...(rest as any) };
};

export const useDeleteSchedule = (): {
  delete: (payload: DeleteScheduleRequest) => Promise<MessageResponseModel>;
} & DeleteScheduleMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    DeleteScheduleRequest
  >('deleteSchedule', (payload: DeleteScheduleRequest) =>
    client.courses.deleteSchedule(payload.id, payload.source),
  );

  return { delete: mutateAsync, ...(rest as any) };
};
